import React from "react";

import {
    BLOCK_MAP,
    COMPOSITE_DECORATORS,
    DefaultBlockTypes,
    DefaultEntityTypes,
    DefaultStyleTypes,
    ENTITY_DECORATORS,
    STYLE_MAP,
} from "./defaults";
import { render } from "./render";
import { renderToReact, resetKeyCounter } from "./tree";
import { Config, ContentState, EntityDecorators } from "./types";

// Extract block type keys from user config
type ExtractBlockKeys<C> = C extends { blockMap?: infer BM }
    ? BM extends Record<infer K, unknown>
        ? K extends string
            ? K
            : never
        : never
    : never;

// Extract style type keys from user config
type ExtractStyleKeys<C> = C extends { styleMap?: infer SM }
    ? SM extends Record<infer K, unknown>
        ? K extends string
            ? K
            : never
        : never
    : never;

// Extract entity type keys from user config
type ExtractEntityKeys<C> = C extends { entityDecorators?: infer ED }
    ? ED extends Record<infer K, unknown>
        ? K extends string
            ? K
            : never
        : never
    : never;

// Combine default types with extracted user config types using union
type MergedConfigTypes<UserConfig> = Config<
    DefaultBlockTypes | ExtractBlockKeys<UserConfig>,
    DefaultStyleTypes | ExtractStyleKeys<UserConfig>,
    DefaultEntityTypes | ExtractEntityKeys<UserConfig>
>;

/**
 * RichText component - renders Wagtail/Draft.js ContentState to React elements.
 *
 * Uses a two-pass approach:
 * 1. Build an intermediate tree using command-based state machines
 * 2. Render the tree to React elements
 *
 * This approach correctly handles:
 * - Overlapping inline styles
 * - Entities wrapping styles (not vice versa)
 * - Nested lists with arbitrary depth
 * - Composite decorators (regex-based text transformations)
 */
export default function RichText<
    UserConfig extends
        | Partial<Config<string, string, string>>
        | undefined = undefined,
>({
    data,
    config,
}: {
    config?: UserConfig;
    data: ContentState<MergedConfigTypes<UserConfig>>;
}): React.ReactNode {
    // Reset key counter for deterministic server rendering
    resetKeyCounter();

    // Merge user config with defaults
    const blockMap = { ...BLOCK_MAP, ...config?.blockMap };
    const styleMap = { ...STYLE_MAP, ...config?.styleMap };
    const entityDecorators = {
        ...ENTITY_DECORATORS,
        ...config?.entityDecorators,
    } as EntityDecorators<unknown>; // Todo: add runtime checks (io-ts?)
    const mergedConfig = {
        blockMap,
        styleMap,
        entityDecorators,
        compositeDecorators:
            config?.compositeDecorators ?? COMPOSITE_DECORATORS,
    } satisfies Config;

    // Build tree and render to React
    const tree = render(data, mergedConfig);
    return <>{renderToReact(tree)}</>;
}

export type { Config, CompositeDecorator, EntityComponent } from "./types";
export { COMPOSITE_DECORATORS } from "./defaults";
